import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FiArrowLeft, FiLock, FiMail, FiAlertTriangle, FiCheck, FiLogOut } from 'react-icons/fi';
import { useAuth } from '@/hooks/useAuth';
import {
  getIdToken,
  changePassword,
  requestEmailChange,
  confirmEmailChange,
} from '../lib/auth';
import { deleteProfile } from '../lib/api';
import DeleteAccountModal from '@/components/settings/DeleteAccountModal';
import Footer from '../components/ui/Footer';
import LanguageSelector from '@/components/ui/LanguageSelector';

type EmailStep = 'request' | 'verify';

export default function Settings() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, signOut, refreshUser } = useAuth();

  // Password change state
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [passwordSuccess, setPasswordSuccess] = useState(false);

  // Email change state
  const [emailStep, setEmailStep] = useState<EmailStep>('request');
  const [newEmail, setNewEmail] = useState('');
  const [emailCode, setEmailCode] = useState('');
  const [emailDestination, setEmailDestination] = useState('');
  const [emailLoading, setEmailLoading] = useState(false);
  const [emailError, setEmailError] = useState<string | null>(null);
  const [emailSuccess, setEmailSuccess] = useState(false);

  // Delete account state
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);

  // Password change handler
  async function handlePasswordChange(e: React.FormEvent) {
    e.preventDefault();
    setPasswordError(null);
    setPasswordSuccess(false);

    if (newPassword !== confirmPassword) {
      setPasswordError(t('settings.errors.passwordMismatch'));
      return;
    }
    if (newPassword.length < 8) {
      setPasswordError(t('settings.errors.passwordTooShort'));
      return;
    }

    setPasswordLoading(true);
    try {
      await changePassword(currentPassword, newPassword);
      setPasswordSuccess(true);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setTimeout(() => setPasswordSuccess(false), 3000);
    } catch (err) {
      const message = err instanceof Error ? err.message : '';
      if (message.includes('NotAuthorizedException') || message.includes('Incorrect')) {
        setPasswordError(t('settings.errors.incorrectPassword'));
      } else if (message.includes('InvalidPasswordException') || message.includes('Password')) {
        setPasswordError(t('settings.errors.passwordTooWeak'));
      } else {
        setPasswordError(t('settings.errors.passwordChangeFailed'));
      }
    } finally {
      setPasswordLoading(false);
    }
  }

  // Email change request handler
  async function handleEmailRequest(e: React.FormEvent) {
    e.preventDefault();
    setEmailError(null);
    setEmailLoading(true);

    try {
      const result = await requestEmailChange(newEmail);
      setEmailDestination(result.destination);
      setEmailStep('verify');
    } catch (err) {
      const message = err instanceof Error ? err.message : '';
      if (message.includes('AliasExistsException') || message.includes('already exists')) {
        setEmailError(t('settings.errors.emailInUse'));
      } else {
        setEmailError(t('settings.errors.emailChangeFailed'));
      }
    } finally {
      setEmailLoading(false);
    }
  }

  // Email verification handler
  async function handleEmailVerify(e: React.FormEvent) {
    e.preventDefault();
    setEmailError(null);
    setEmailLoading(true);

    try {
      await confirmEmailChange(emailCode);
      await refreshUser();
      setEmailSuccess(true);
      setEmailStep('request');
      setNewEmail('');
      setEmailCode('');
      setTimeout(() => setEmailSuccess(false), 3000);
    } catch (err) {
      const message = err instanceof Error ? err.message : '';
      if (message.includes('CodeMismatchException') || message.includes('Invalid')) {
        setEmailError(t('settings.errors.invalidCode'));
      } else if (message.includes('ExpiredCodeException') || message.includes('expired')) {
        setEmailError(t('settings.errors.codeExpired'));
      } else {
        setEmailError(t('settings.errors.emailChangeFailed'));
      }
    } finally {
      setEmailLoading(false);
    }
  }

  // Back to email input
  function handleBackToEmailInput() {
    setEmailStep('request');
    setEmailCode('');
    setEmailError(null);
  }

  // Delete account handler
  async function handleDeleteAccount() {
    const token = await getIdToken();
    if (!token) {
      navigate('/login');
      return;
    }
    await deleteProfile(token);
    signOut();
    navigate('/');
  }

  // Sign out handler
  function handleSignOut() {
    signOut();
    navigate('/');
  }

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      {/* Header */}
      <header className="bg-gray-900">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            {/* Logo + Back button */}
            <div className="flex items-center gap-4">
              <h1 className="text-xl font-bold">
                <span className="text-white">MyPink</span>
                <span className="text-mk-pink-400">Contact</span>
              </h1>
              <div className="hidden sm:block h-6 w-px bg-gray-700" />
              <button
                onClick={() => navigate('/dashboard')}
                className="flex items-center gap-2 text-gray-300 hover:text-white transition-colors"
              >
                <FiArrowLeft className="w-5 h-5" />
                <span className="hidden sm:inline">{t('settings.backToDashboard')}</span>
              </button>
            </div>

            {/* Right side */}
            <div className="flex items-center gap-4">
              <LanguageSelector variant="light" />
              <button
                onClick={handleSignOut}
                className="p-2 text-gray-400 hover:text-white transition-colors"
                title={t('nav.signOut')}
              >
                <FiLogOut className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Main content */}
      <div className="flex-1 py-8 px-4">
        <div className="max-w-2xl mx-auto space-y-6">
          {/* Page title */}
          <div className="text-center mb-8">
            <h1 className="text-2xl font-bold text-gray-900">{t('settings.title')}</h1>
            <p className="text-gray-600 mt-2">{t('settings.subtitle')}</p>
          </div>

          {/* Change Password Section */}
          <div className="bg-white rounded-2xl shadow-lg p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-mk-pink-100 rounded-full flex items-center justify-center">
                <FiLock className="w-5 h-5 text-mk-pink-600" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900">
                  {t('settings.changePassword.title')}
                </h2>
                <p className="text-sm text-gray-500">
                  {t('settings.changePassword.description')}
                </p>
              </div>
            </div>

            <form onSubmit={handlePasswordChange} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('settings.changePassword.currentPassword')}
                </label>
                <input
                  type="password"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-mk-pink-500 focus:border-transparent"
                  placeholder={t('settings.changePassword.currentPasswordPlaceholder')}
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('settings.changePassword.newPassword')}
                </label>
                <input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-mk-pink-500 focus:border-transparent"
                  placeholder={t('settings.changePassword.newPasswordPlaceholder')}
                  required
                  minLength={8}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('settings.changePassword.confirmPassword')}
                </label>
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-mk-pink-500 focus:border-transparent"
                  placeholder={t('settings.changePassword.confirmPasswordPlaceholder')}
                  required
                />
              </div>

              {passwordError && <p className="text-red-500 text-sm">{passwordError}</p>}
              {passwordSuccess && (
                <p className="text-green-600 text-sm flex items-center gap-2">
                  <FiCheck className="w-4 h-4" />
                  {t('settings.changePassword.success')}
                </p>
              )}

              <button
                type="submit"
                disabled={passwordLoading}
                className="w-full py-3 bg-mk-pink-600 text-white rounded-lg font-semibold hover:bg-mk-pink-700 transition-colors disabled:opacity-50"
              >
                {passwordLoading
                  ? t('settings.changePassword.updating')
                  : t('settings.changePassword.button')}
              </button>
            </form>
          </div>

          {/* Change Email Section */}
          <div className="bg-white rounded-2xl shadow-lg p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-mk-pink-100 rounded-full flex items-center justify-center">
                <FiMail className="w-5 h-5 text-mk-pink-600" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900">
                  {t('settings.changeEmail.title')}
                </h2>
                <p className="text-sm text-gray-500">
                  {t('settings.changeEmail.description')}
                </p>
              </div>
            </div>

            {/* Current email display */}
            <div className="mb-4 p-3 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-500">{t('settings.changeEmail.currentEmail')}</p>
              <p className="font-medium text-gray-900">{user?.email}</p>
            </div>

            {emailStep === 'request' ? (
              <form onSubmit={handleEmailRequest} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('settings.changeEmail.newEmail')}
                  </label>
                  <input
                    type="email"
                    value={newEmail}
                    onChange={(e) => setNewEmail(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-mk-pink-500 focus:border-transparent"
                    placeholder={t('settings.changeEmail.newEmailPlaceholder')}
                    required
                  />
                </div>

                {emailError && <p className="text-red-500 text-sm">{emailError}</p>}
                {emailSuccess && (
                  <p className="text-green-600 text-sm flex items-center gap-2">
                    <FiCheck className="w-4 h-4" />
                    {t('settings.changeEmail.success')}
                  </p>
                )}

                <button
                  type="submit"
                  disabled={emailLoading}
                  className="w-full py-3 bg-mk-pink-600 text-white rounded-lg font-semibold hover:bg-mk-pink-700 transition-colors disabled:opacity-50"
                >
                  {emailLoading
                    ? t('settings.changeEmail.requesting')
                    : t('settings.changeEmail.requestButton')}
                </button>
              </form>
            ) : (
              <form onSubmit={handleEmailVerify} className="space-y-4">
                <p className="text-sm text-gray-600 mb-4">
                  {t('settings.changeEmail.codeSent', { email: emailDestination })}
                </p>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('settings.changeEmail.verificationCode')}
                  </label>
                  <input
                    type="text"
                    value={emailCode}
                    onChange={(e) => setEmailCode(e.target.value.replace(/\D/g, ''))}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-mk-pink-500 focus:border-transparent text-center text-2xl tracking-widest font-mono"
                    placeholder="000000"
                    maxLength={6}
                    required
                    autoFocus
                  />
                </div>

                {emailError && <p className="text-red-500 text-sm">{emailError}</p>}

                <button
                  type="submit"
                  disabled={emailLoading || emailCode.length < 6}
                  className="w-full py-3 bg-mk-pink-600 text-white rounded-lg font-semibold hover:bg-mk-pink-700 transition-colors disabled:opacity-50"
                >
                  {emailLoading
                    ? t('settings.changeEmail.verifying')
                    : t('settings.changeEmail.verifyButton')}
                </button>

                <button
                  type="button"
                  onClick={handleBackToEmailInput}
                  className="w-full text-gray-500 hover:text-gray-700 text-sm"
                >
                  {t('settings.changeEmail.changeEmail')}
                </button>
              </form>
            )}
          </div>

          {/* Danger Zone Section */}
          <div className="bg-white rounded-2xl shadow-lg p-6 border-2 border-red-100">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                <FiAlertTriangle className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900">
                  {t('settings.dangerZone.title')}
                </h2>
                <p className="text-sm text-gray-500">
                  {t('settings.dangerZone.description')}
                </p>
              </div>
            </div>

            <button
              onClick={() => setDeleteModalOpen(true)}
              className="w-full py-3 text-red-600 border-2 border-red-300 hover:border-red-400 hover:bg-red-50 rounded-lg font-semibold transition-colors"
            >
              {t('deleteAccount.button')}
            </button>
          </div>

        </div>
      </div>

      <Footer showCta={false} />

      {/* Delete Account Modal */}
      <DeleteAccountModal
        isOpen={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        onConfirm={handleDeleteAccount}
      />
    </div>
  );
}
